import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { courseService, Course, Lesson } from '../../api/courseService';
import { theme } from '../../styles/theme';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { AxiosResponse } from 'axios';
import { getVideoUrl } from '../../utils';

const LessonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const LessonHeader = styled.div`
  margin-bottom: ${theme.spacing.md};
  
  h1 {
    font-size: ${theme.fontSizes.xxlarge};
    margin-bottom: ${theme.spacing.sm};
  }
  
  p {
    font-size: ${theme.fontSizes.medium};
    color: ${theme.colors.gray};
  }
`;

const CourseNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
`;

const LessonContent = styled(Card)`
  font-size: ${theme.fontSizes.medium};
  line-height: 1.8;
  
  p {
    margin-bottom: ${theme.spacing.md};
  }
`;

const VideoContainer = styled(Card)`
  padding: 0;
  overflow: hidden;
  border-radius: ${theme.radii.medium};
  margin-bottom: ${theme.spacing.md};
  
  video {
    width: 100%;
    display: block;
  }
`;

const LessonProgress = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

interface ProgressBarProps {
  $progress: number;
}

const ProgressBar = styled.div<ProgressBarProps>`
  flex: 1;
  height: 8px;
  background-color: ${theme.colors.light};
  border-radius: ${theme.radii.medium};
  margin: 0 ${theme.spacing.md};
  overflow: hidden;
  
  div {
    height: 100%;
    background-color: ${theme.colors.primary};
    width: ${props => `${props.$progress}%`};
    transition: width 0.3s ease;
  }
`;

const LessonNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${theme.spacing.lg};
`;

const LoadingContainer = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${theme.colors.gray};
`;

const ErrorContainer = styled.div`
  padding: ${theme.spacing.md};
  color: ${theme.colors.danger};
  text-align: center;
`;

const VideoPlayer = styled.video`
  width: 100%;
  height: auto;
  background-color: #000;
`;

const LessonPage: React.FC = () => {
  const { courseId, lessonId } = useParams<{ courseId: string, lessonId: string }>();
  const navigate = useNavigate();
  
  const { data: courseData, isLoading: courseLoading, error: courseError } = useQuery<AxiosResponse<{success: boolean, data: Course}>, Error>({
    queryKey: ['course', courseId],
    queryFn: () => courseService.getCourseById(courseId || ''),
    enabled: !!courseId,
  });
  
  const { data: lessonsData, isLoading: lessonsLoading, error: lessonsError } = useQuery<AxiosResponse<{success: boolean, data: Lesson[]}>, Error>({
    queryKey: ['lessons', courseId],
    queryFn: () => courseService.getLessons(courseId || ''),
    enabled: !!courseId && !courseData?.data?.data?.lessons,
  });

  // Set up mutation for updating lesson progress
  const progressMutation = useMutation({
    mutationFn: ({ completed }: { completed: boolean }) => 
      courseService.updateLessonProgress(courseId || '', lessonId || '', completed),
    onSuccess: () => {
      console.log(`Updated progress for lesson ${lessonId}, completed: ${true}`);
    },
    onError: (error) => {
      console.error('Error updating lesson progress:', error);
    }
  });

  // Mark lesson as viewed when component mounts
  useEffect(() => {
    if (courseId && lessonId) {
      console.log(`Marking lesson ${lessonId} as viewed`);
      progressMutation.mutate({ completed: false });
    }
  }, [courseId, lessonId]);

  if (courseLoading || lessonsLoading) {
    return (
      <LessonContainer>
        <LoadingContainer>Loading lesson...</LoadingContainer>
      </LessonContainer>
    );
  }

  if (courseError || lessonsError) {
    console.error('Error loading lesson:', courseError || lessonsError);
    return (
      <LessonContainer>
        <ErrorContainer>Unable to load lesson. Please try again later.</ErrorContainer>
        <Button as={Link} to={`/courses/${courseId}`}>Back to Course</Button>
      </LessonContainer>
    );
  }

  // Get course data
  const course = courseData?.data?.data;
  if (!course) {
    return (
      <LessonContainer>
        <ErrorContainer>Course not found</ErrorContainer>
        <Button as={Link} to="/courses">Back to Courses</Button>
      </LessonContainer>
    );
  }
  
  // Get lessons either from course data or from separate lessons query
  const lessons = course.lessons || lessonsData?.data?.data || [];
  
  console.log('Lessons data:', lessons);
  
  // Find the current lesson - handle both id as string and number
  const currentLesson = lessons.find(lesson => {
    const lessonIdString = String(lesson.id);
    return lessonIdString === lessonId;
  });
  
  console.log('Current lesson:', currentLesson, 'looking for lessonId:', lessonId);
  
  if (!currentLesson) {
    return (
      <LessonContainer>
        <ErrorContainer>Lesson not found. ID: {lessonId}</ErrorContainer>
        <Button as={Link} to={`/courses/${courseId}`}>Back to Course</Button>
      </LessonContainer>
    );
  }
  
  // Get the current lesson index and calculate next/previous lessons
  const currentIndex = lessons.findIndex(lesson => {
    const lessonIdString = String(lesson.id);
    return lessonIdString === lessonId;
  });
  
  const previousLesson = currentIndex > 0 ? lessons[currentIndex - 1] : null;
  const nextLesson = currentIndex < lessons.length - 1 ? lessons[currentIndex + 1] : null;
  
  // Calculate progress in course
  const progress = ((currentIndex + 1) / lessons.length) * 100;
  
  // Handler for completing the lesson
  const handleComplete = () => {
    // Mark lesson as completed
    if (courseId && lessonId) {
      console.log(`Marking lesson ${lessonId} as completed`);
      progressMutation.mutate({ completed: true });
      
      // Navigate to next lesson or back to course
      if (nextLesson) {
        navigate(`/courses/${courseId}/lessons/${nextLesson.id}`);
      } else {
        navigate(`/courses/${courseId}`);
      }
    }
  };
  
  // Format content with paragraphs
  const formattedContent = currentLesson.content.split('\n\n').map((paragraph, index) => (
    <p key={index}>{paragraph}</p>
  ));

  // Determine if there is a video to be displayed
  const hasVideo = !!currentLesson.video_url;

  return (
    <LessonContainer>
      <CourseNavigation>
        <Button variant="outline" as={Link} to={`/courses/${courseId}`}>
          Back to Course
        </Button>
        <LessonProgress>
          <span>Progress:</span>
          <ProgressBar $progress={progress}>
            <div />
          </ProgressBar>
          <span>{`${currentIndex + 1}/${lessons.length}`}</span>
        </LessonProgress>
      </CourseNavigation>
      
      <LessonHeader>
        <h1>{currentLesson.title}</h1>
        <p>Lesson {currentIndex + 1} of {lessons.length}</p>
      </LessonHeader>
      
      {hasVideo && (
        <VideoContainer>
          <VideoPlayer 
            src={getVideoUrl(currentLesson.video_url)} 
            controls
            poster={currentLesson.thumbnail_url ? getVideoUrl(currentLesson.thumbnail_url) : undefined}
            controlsList="nodownload"
            playsInline
          />
        </VideoContainer>
      )}
      
      <LessonContent>
        {formattedContent}
      </LessonContent>
      
      <LessonNavigation>
        {previousLesson ? (
          <Button 
            variant="outline" 
            as={Link} 
            to={`/courses/${courseId}/lessons/${previousLesson.id}`}
          >
            Previous Lesson
          </Button>
        ) : (
          <div></div>
        )}
        
        {nextLesson ? (
          <Button onClick={handleComplete}>
            Complete & Continue
          </Button>
        ) : (
          <Button onClick={handleComplete}>
            Complete Course
          </Button>
        )}
      </LessonNavigation>
    </LessonContainer>
  );
};

export default LessonPage; 